import { call, put, select, takeLatest } from 'redux-saga/effects'

import {
  getFormDbId,
  getOrgSfid,
  getPromoCodeSessionId,
  getRegistrationProducts,
  getValidatedPromoCodes,
} from 'selectors'

import { promocode as promocodeActions } from '../constants/actions'
import { promocode } from '../constants/endpoints'
import { post } from '../utils/request'

export function* updateSession() {
  const sessionId = yield select(getPromoCodeSessionId)
  const validatedPromoCodes = yield select(getValidatedPromoCodes)

  yield post({
    endpointId: promocode.sessions,
    data: {
      validatedPromoCodes: validatedPromoCodes.map(p => p.code.id),
      sessionId,
    },
  })
}

export function* resetSession() {
  const sessionId = yield select(getPromoCodeSessionId)

  yield post({
    endpointId: promocode.sessions,
    data: {
      validatedPromoCodes: [],
      sessionId,
    },
  })
}

export function* validatePromoCode({ payload: codeValue }) {
  const formId = yield select(getFormDbId)
  const organisationId = yield select(getOrgSfid)
  const sessionId = yield select(getPromoCodeSessionId)

  const response = yield post({
    endpointId: promocode.validate,
    data: {
      codeValue,
      formId,
      organisationId,
      sessionId,
    },
  })

  return response
}

export function* addPromoCode({ payload: codeValue }) {
  yield put(promocodeActions.setPromoCodeValue(codeValue))
  yield put(promocodeActions.promoCodeError(null))

  // check code isn't already added
  const validatedPromoCodes = yield select(getValidatedPromoCodes)
  const alreadyApplied = validatedPromoCodes.some(p => p.code.codeValue === codeValue)

  if (alreadyApplied) {
    yield put(promocodeActions.promoCodeError('You have already added this promo code to your order'))
    return
  }

  if (validatedPromoCodes.length === 1) {
    yield put(
      promocodeActions.promoCodeError(
        'Only one promo code can be applied at checkout. Please remove your current promo code to apply another'
      )
    )
    return
  }

  // validate code
  const validationResponse = yield call(validatePromoCode, { payload: codeValue })

  // biome-ignore lint/complexity/useOptionalChain: TODO(biome-2.4): newly detected in 2.4 upgrade
  if (!validationResponse || !validationResponse.success) {
    const { errorMessage } = validationResponse || {}
    yield put(promocodeActions.promoCodeError(errorMessage || 'Something went wrong'))
    return
  }

  // check promo code's products are in registration
  const { products = [] } = validationResponse.data || {}
  const promoCodeProductsIds = (products || []).map(p => p.sfid)

  const registrationProducts = yield select(getRegistrationProducts)
  const registrationProductIds = Object.keys(registrationProducts || {})

  const registrationHasProducts = promoCodeProductsIds.some(sfid => registrationProductIds.includes(sfid))

  if (!registrationHasProducts) {
    yield put(promocodeActions.promoCodeError('You have no products in your order applicable to this promo code'))
    return
  }

  yield put(promocodeActions.setPromoCodeValue(''))
  yield put(
    promocodeActions.updatePromoCode({
      add: validationResponse.data,
    })
  )

  yield call(updateSession)
}

export function* removePromoCode({ payload: codeId }) {
  yield put(
    promocodeActions.updatePromoCode({
      remove: codeId,
    })
  )

  yield call(updateSession)
}

export function* runPromoCodeSagas() {
  yield takeLatest(promocodeActions.UPDATE_PROMOCODE_SESSION, updateSession)
  yield takeLatest(promocodeActions.RESET_PROMOCODE_SESSION, updateSession)
  yield takeLatest(promocodeActions.ADD_PROMOCODE, addPromoCode)
  yield takeLatest(promocodeActions.REMOVE_PROMOCODE, removePromoCode)
  yield takeLatest(promocodeActions.VALIDATE_PROMOCODE, validatePromoCode)
}
